<script setup lang="ts">
import Badge from '@/components/ui/Badge.vue';
import { Building, Target, Users } from 'lucide-vue-next';
</script>

<template>
    <section id="about" class="py-20">
        <div class="container mx-auto px-4">
            <div class="mb-16 text-center">
                <Badge variant="secondary" class="mb-4">Project Mission</Badge>
                <h2 class="mb-6">A practical layer of trust for physical cash</h2>
                <p class="mx-auto max-w-3xl text-xl leading-relaxed text-muted-foreground">
                    Digital Safe connects physical banknotes with digital records, making ownership documentation, verification and asset protection
                    easier to manage.
                </p>
            </div>

            <div class="grid grid-cols-1 items-center gap-12 lg:grid-cols-2">
                <div class="glass-card glow-hover">
                    <h3 class="mb-6 text-2xl font-bold text-primary">Why Digital Safe exists</h3>
                    <p class="mb-6 leading-relaxed text-muted-foreground">
                        CyfrowyZłoty sp. z o.o., supported by practitioners and industry experts, is building a platform for documenting and securing
                        monetary assets held by households, companies and institutions.
                    </p>
                    <p class="leading-relaxed text-muted-foreground">
                        The platform is designed to help users create verifiable digital certificates, preserve important asset details and use
                        blockchain transparency to make the verification process more reliable.
                    </p>
                </div>

                <div class="space-y-6">
                    <div class="glass-card glow-hover">
                        <div class="mb-4 flex items-center space-x-4">
                            <div class="glow flex h-12 w-12 items-center justify-center rounded-lg bg-gradient-hero">
                                <Users class="h-6 w-6 text-primary-foreground" />
                            </div>
                            <div>
                                <h4 class="text-lg font-semibold">Households</h4>
                                <p class="text-sm text-muted-foreground">Private savings and family records</p>
                            </div>
                        </div>
                        <p class="text-sm text-muted-foreground">Document valuable cash holdings and keep essential proof organized in one place.</p>
                    </div>

                    <div class="glass-card glow-hover">
                        <div class="mb-4 flex items-center space-x-4">
                            <div class="glow flex h-12 w-12 items-center justify-center rounded-lg bg-gradient-hero">
                                <Building class="h-6 w-6 text-primary-foreground" />
                            </div>
                            <div>
                                <h4 class="text-lg font-semibold">Businesses</h4>
                                <p class="text-sm text-muted-foreground">Operational cash and internal control</p>
                            </div>
                        </div>
                        <p class="text-sm text-muted-foreground">Support documentation, audits and verification of cash assets used in daily operations.</p>
                    </div>

                    <div class="glass-card glow-hover">
                        <div class="mb-4 flex items-center space-x-4">
                            <div class="glow flex h-12 w-12 items-center justify-center rounded-lg bg-gradient-hero">
                                <Target class="h-6 w-6 text-primary-foreground" />
                            </div>
                            <div>
                                <h4 class="text-lg font-semibold">Institutions</h4>
                                <p class="text-sm text-muted-foreground">Structured verification workflows</p>
                            </div>
                        </div>
                        <p class="text-sm text-muted-foreground">Create a repeatable process for registering, checking and confirming asset records.</p>
                    </div>
                </div>
            </div>
        </div>
    </section>
</template>
